import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Spinner, Alert } from 'react-bootstrap';
import api from '@services/api'; // Assurez-vous que le chemin est correct
import { toast } from 'react-toastify';

const ReplaceParticipantModal = ({
  show,
  onHide,
  participant,
  participantType,
  salonId,
  onReplacementSuccess
}) => {
  const [formData, setFormData] = useState({
    replacement_firstname: '',
    replacement_lastname: '',
    replacement_email: '',
    replacement_phone: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (participant) {
      setFormData({
        replacement_firstname: participant.replacement_firstname || '',
        replacement_lastname: participant.replacement_lastname || '',
        replacement_email: participant.replacement_email || '',
        replacement_phone: participant.replacement_phone || ''
      });
    } else {
      // Reset form if no participant or closing modal
      setFormData({
        replacement_firstname: '',
        replacement_lastname: '',
        replacement_email: '',
        replacement_phone: ''
      });
      setError(null);
    }
  }, [participant, show]);

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (mountedRef.current) {
      setIsLoading(true);
      setError(null);
    } else {
      return;
    }

    const endpoint = `/admin/partner-salons/${salonId}/participants/${participantType}/${participant.id}/replace`;

    try {
      const response = await api.put(endpoint, formData);

      if (response.data.success && response.data.data) {
        // Vérifier la présence du salon mis à jour
        toast(
          `Participant ${participant.firstname} ${participant.lastname} remplacé avec succès.`
        );
        if (onReplacementSuccess) {
          // Passer le salon complet et mis à jour (contenu dans response.data.data)
          onReplacementSuccess(response.data.data);
        }
        onHide();
      } else {
        const errorMessage =
          response.data.message || 'Erreur lors du remplacement.';
        if (mountedRef.current) setError(errorMessage);
        toast(errorMessage);
      }
    } catch (err) {
      console.error('Erreur lors du remplacement du participant:', err);
      const errorMessage =
        err.response?.data?.message || 'Une erreur est survenue.';
      if (mountedRef.current) setError(errorMessage);
      toast(errorMessage);
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  };

  if (!participant) return null;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Remplacer {participant.firstname} {participant.lastname}
          {participant.is_replaced && ' (déjà remplacé)'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <p>
            Participant actuel :{' '}
            <strong>
              {participant.firstname} {participant.lastname}
            </strong>
            {participantType === 'company' &&
              participant.company &&
              ` (${participant.company.name})`}
            {participantType === 'partner' &&
              participant.partner &&
              ` (${participant.partner.name})`}
          </p>
          <hr />
          <h5>Informations du remplaçant</h5>
          <Form.Group className="mb-3">
            <Form.Label>Prénom du remplaçant *</Form.Label>
            <Form.Control
              type="text"
              name="replacement_firstname"
              value={formData.replacement_firstname}
              onChange={handleChange}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Nom du remplaçant *</Form.Label>
            <Form.Control
              type="text"
              name="replacement_lastname"
              value={formData.replacement_lastname}
              onChange={handleChange}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Email du remplaçant</Form.Label>
            <Form.Control
              type="email"
              name="replacement_email"
              value={formData.replacement_email}
              onChange={handleChange}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Téléphone du remplaçant</Form.Label>
            <Form.Control
              type="text"
              name="replacement_phone"
              value={formData.replacement_phone}
              onChange={handleChange}
            />
          </Form.Group>
          <small>* Champs obligatoires</small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={isLoading}>
            Annuler
          </Button>
          <Button variant="primary" type="submit" disabled={isLoading}>
            {isLoading ? (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              'Enregistrer Remplacement'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

ReplaceParticipantModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  participant: PropTypes.shape({
    id: PropTypes.number,
    firstname: PropTypes.string,
    lastname: PropTypes.string,
    is_replaced: PropTypes.bool,
    replacement_firstname: PropTypes.string,
    replacement_lastname: PropTypes.string,
    replacement_email: PropTypes.string,
    replacement_phone: PropTypes.string,
    company: PropTypes.shape({
      name: PropTypes.string
    }),
    partner: PropTypes.shape({
      name: PropTypes.string
    })
  }),
  participantType: PropTypes.oneOf(['company', 'partner']).isRequired,
  salonId: PropTypes.number.isRequired,
  onReplacementSuccess: PropTypes.func.isRequired
};

export default ReplaceParticipantModal;
